import { ApplicationConfig, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, RouteReuseStrategy, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  APP_CONTEXT,
  AppContextEnum,
  CHUNK_ERROR_FALLBACK_HANDLER,
  provideIdentityToastNotifier,
  provideVersionCheckToast,
  URL_FLAGS,
} from '@lite-workspace/utils';
import {
  AnalyticsErrorHandler,
  AnalyticsEventName,
  AnalyticsService,
  IMAGE_ERROR_TRACKER,
  provideLumirEntryCookie,
} from '@lite-workspace/shared/services/analytics';
import { FILE_PICKER_TELEMETRY, provideUploadHttpInterceptors, type FilePickerTelemetry } from '@lite-workspace/uploader-core';
import { HttpIdentityData, IDENTITY_DATA } from '@lite-workspace/shared/identity';
import { ONBOARDING_HOST_AUDIENCE, provideOnboarding } from '@lite-workspace/shared/onboarding-service';
import { connectSupportWidget } from '@lite-workspace/shared/support-widget';
import { appRoutes } from './app.routes';
import { AlbumEditorReuseStrategy } from './core/album-editor-reuse.strategy';
import { APP_HTTP_INTERCEPTORS } from './core/http-interceptors';
import { initAuthMode } from './core/bff';
import { purgeLegacyClientTokens } from './core/auth';
import { installIdentityWatch } from './core/identity-watch';
import { AnalyticsIdentityService } from './core/analytics-identity.service';
import { supportWidgetZone } from './core/support-widget-zone';
import { ProjectRoleService } from './core/project-role';
import { THEME_OPTIONS, ThemeService } from '@lite-workspace/shared/theme';
import { provideProductToasts, ToastService } from '@lite-workspace/shared/helm-ui/toast';
import { EDITOR_NOTIFIER } from '@lite-workspace/shared/design-editor/ports';
import { PROJECTS_DATA } from './features/projects/domain/infrastructure/projects.data';
import { HttpProjectsData } from './features/projects/domain/infrastructure/http-projects.data';
// eslint-disable-next-line @nx/enforce-module-boundaries -- provideAlbumEditor for the lazy album editor route
import { provideAlbumEditor } from '@lite-workspace/album-editor/ports';
import { provideAlbumEditorHostRoot } from './core/album-editor-root.providers';
import { provideRetouchRoot } from './core/retouch-host.providers';
import { ACCOUNT_DATA } from './features/account/domain/infrastructure/account.data';
import { HttpAccountData } from './features/account/domain/infrastructure/http-account.data';
import { DESIGNS_DATA } from './features/designs/domain/infrastructure/designs.data';
import { HttpDesignsData } from './features/designs/domain/infrastructure/http-designs.data';
import { provideSpartanHlm } from '@lite-workspace/shared/helm-ui/utils';
import { WIZARD_ACCOUNT_SLOT } from '@lite-workspace/shared/blocks/wizard-shell';
import { AccountMenuHostComponent } from './features/account/ui/account-menu.component';
import { NOTIFICATIONS_HOST_PRODUCT } from '@lite-workspace/shared/blocks/notifications';
import { loadLayoutEngineFlag } from '@lite-workspace/shared/photobook-shared/layout-engine-flag';

export const appConfig: ApplicationConfig = {
  providers: [
    // Synchronous initializer: the entry is stored before the first navigation can redirect to sign-in and lose the referrer.
    provideLumirEntryCookie(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes, withComponentInputBinding(), withRouterConfig({ paramsInheritanceStrategy: 'always' })),
    { provide: RouteReuseStrategy, useExisting: AlbumEditorReuseStrategy },
    provideHttpClient(withFetch(), withInterceptors(APP_HTTP_INTERCEPTORS)),
    provideUploadHttpInterceptors(APP_HTTP_INTERCEPTORS),
    { provide: CHUNK_ERROR_FALLBACK_HANDLER, useClass: AnalyticsErrorHandler },
    { provide: IMAGE_ERROR_TRACKER, useExisting: AnalyticsErrorHandler },
    { provide: APP_CONTEXT, useValue: AppContextEnum.ADMIN },
    provideSpartanHlm(),
    provideProductToasts(),
    provideVersionCheckToast(),
    // Watch ставится строго ПОСЛЕ initAuthMode: его базовая точка отсчёта — bffUserId бут-probe'а.
    provideAppInitializer(() => initAuthMode().then(() => installIdentityWatch())),
    provideAppInitializer(() => purgeLegacyClientTokens()),
    { provide: THEME_OPTIONS, useValue: { defaultPreference: 'dark', storageKey: 'pcl-theme', sharedCookie: 'write' } },
    provideAppInitializer(() => void inject(ThemeService)),
    // Read before the first navigation: a redirect would drop `?ai-retouch=1` / `?llm-chat=1`.
    provideAppInitializer(() => void inject(URL_FLAGS)),
    provideAppInitializer(() => void inject(AnalyticsIdentityService)),
    provideAppInitializer(() => connectSupportWidget(supportWidgetZone)),
    provideAppInitializer(() => loadLayoutEngineFlag()),
    { provide: PROJECTS_DATA, useClass: HttpProjectsData },
    provideAlbumEditor(),
    ...provideAlbumEditorHostRoot(),
    ...provideRetouchRoot(),
    provideOnboarding(),
    // Онбординг ведёт фотографа: сессия в урезанном хроме организатора его подсказок не видит.
    {
      provide: ONBOARDING_HOST_AUDIENCE,
      useFactory: (): (() => boolean) => {
        const role = inject(ProjectRoleService);
        return () => !role.isOrganizerOnly();
      },
    },
    { provide: ACCOUNT_DATA, useClass: HttpAccountData },
    { provide: IDENTITY_DATA, useClass: HttpIdentityData },
    provideIdentityToastNotifier(),
    { provide: DESIGNS_DATA, useClass: HttpDesignsData },
    { provide: WIZARD_ACCOUNT_SLOT, useValue: AccountMenuHostComponent },
    { provide: NOTIFICATIONS_HOST_PRODUCT, useValue: 'fotoclass' },
    { provide: EDITOR_NOTIFIER, useExisting: ToastService },
    // A pick that reaches nobody is invisible in the UI and in the console (FKS-10485 died undiagnosed for it) — this
    // is the only signal that says whether the loss still happens in the wild.
    {
      provide: FILE_PICKER_TELEMETRY,
      useFactory: (): FilePickerTelemetry => {
        const analytics = inject(AnalyticsService);
        return {
          silentClose: context => analytics.track(AnalyticsEventName.FILE_PICKER_SILENT_CLOSE, { ...context }),
          pickerRefused: context => analytics.track(AnalyticsEventName.FILE_PICKER_REFUSED, { ...context }),
        };
      },
    },
  ],
};
